// Multi-owner helpers. The task model carries an `owners` array of
// { staffId, role } where role ∈ R/A/C/I. The CEO + eng review locked
// these as the four RACI roles:
//
//   R = Responsible — does the work. At least 1 R required per task.
//   A = Accountable — answers for the outcome. Usually 0 or 1.
//   C = Consulted   — two-way comms before/during work. 0+.
//   I = Informed    — one-way comms when done. 0+.
//
// Legacy v1 tasks had a single `assigneeId` string. We migrate those
// on read (storage.js) to `owners=[{staffId: assigneeId, role: 'R'}]`
// so this module never has to handle the legacy shape.

export type RaciRole = 'R' | 'A' | 'C' | 'I';

export interface Owner {
  staffId: string;
  role: RaciRole;
  isPrimary?: boolean;            // true on the 主负责人 (only meaningful for R)
}

export interface TaskWithOwners {
  owners?: Owner[];
  assigneeId?: string | null;
  primaryAssigneeId?: string | null;
}

export interface StaffLike {
  id: string;
  name?: string;
}

/**
 * Read owners from a task, handling legacy shape. Caller can rely on
 * the returned array being non-undefined.
 */
export function getOwners(task: TaskWithOwners): Owner[] {
  if (task.owners && task.owners.length > 0) return task.owners;
  if (task.assigneeId) return [{ staffId: task.assigneeId, role: 'R' }];
  return [];
}

export function getOwnersByRole(task: TaskWithOwners, role: RaciRole): Owner[] {
  return getOwners(task).filter(o => o.role === role);
}

export function getResponsibles(task: TaskWithOwners): Owner[] {
  // Sort primary first so the rail / pills / list view all show 主负责人 first.
  // Stable: non-primary keeps their original relative order from storage.
  const all = getOwnersByRole(task, 'R');
  return [...all].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
}

/**
 * The 主负责人 (primary R). Returns the explicitly-flagged primary, or
 * the first R as a fallback (legacy rows without is_primary).
 */
export function getPrimaryResponsible(task: TaskWithOwners): Owner | null {
  const rs = getOwnersByRole(task, 'R');
  return rs.find(o => o.isPrimary) ?? rs[0] ?? null;
}

export function getAccountable(task: TaskWithOwners): Owner | null {
  const a = getOwnersByRole(task, 'A');
  return a[0] ?? null;
}

export function getConsulted(task: TaskWithOwners): Owner[] {
  return getOwnersByRole(task, 'C');
}

export function getInformed(task: TaskWithOwners): Owner[] {
  return getOwnersByRole(task, 'I');
}

/**
 * Is this user any kind of owner on the task? Used by MyTasks to filter
 * the user's queue: they see tasks where they're R OR A OR C OR I.
 */
export function isOwner(task: TaskWithOwners, staffId: string): boolean {
  return getOwners(task).some(o => o.staffId === staffId);
}

/**
 * Is this user the primary R? Used by AdminOverview to count
 * "things on this person's plate" (R-only, not C/I).
 */
export function isResponsible(task: TaskWithOwners, staffId: string): boolean {
  return getResponsibles(task).some(o => o.staffId === staffId);
}

/**
 * Validation: every task must have at least one R per eng review §1.1.
 * Worker-side rule; UI gates save when this returns false.
 */
export function hasValidResponsible(task: TaskWithOwners): boolean {
  return getResponsibles(task).length > 0;
}

/**
 * Display helper: comma-separated names of all responsibles.
 * e.g. for the staff column in ListView when showAssignee=true.
 */
export function responsibleNames(task: TaskWithOwners, staff: StaffLike[]): string {
  const rs = getResponsibles(task);
  return rs
    .map(o => staff.find(s => s.id === o.staffId)?.name)
    .filter(Boolean)
    .join(', ');
}

export const ROLE_LABELS: Record<RaciRole, string> = {
  R: '负责',
  A: '审批',
  C: '咨询',
  I: '通知'                                      // was 知会; Regina prefers 通知
};

export const ROLE_DESCRIPTIONS: Record<RaciRole, string> = {
  R: 'Responsible — 实际做事的人',
  A: 'Accountable — 对结果负责的人',
  C: 'Consulted — 做之前 / 中需要双向沟通',
  I: 'Informed — 完成后单向通知'
};
